<template>
  <div v-html="htmlContent" class="markdown_content" :class="customClass"></div>
</template>

<script setup>
import { ref, onMounted, watch } from "vue";
import { marked } from "marked";

const props = defineProps({
  markdownText: {
    type: String,
    required: true,
  },
  customClass: {
    type: String,
    default: "",
  },
});

const htmlContent = ref("");

const processMarkdown = async () => {
  if (props?.markdownText) {
    let cleanedMarkdown = props?.markdownText
      .replace(/```markdown\n/, "")
      .replace(/```/, "");
    cleanedMarkdown = processMarkdownText(cleanedMarkdown);
    htmlContent.value = await convertToHTML(cleanedMarkdown);
  }
};

const processMarkdownText = (text) => {
  text = text.replace(/\\n/g, "\n");
  text = text.replace(/\n{3,}/g, "\n\n");
  text = text.replace(/^(#{1,6} .+)$/gm, "\n$1\n");

  text = text.replace(/^##### (.*$)/gm, "<h5>$1</h5>");
  text = text.replace(/^#### (.*$)/gm, "<h4>$1</h4>");
  text = text.replace(/^### (.*$)/gm, "<h3><strong>$1</strong></h3>");
  text = text.replace(/^## (.*$)/gm, "<h2><strong>$1</strong></h2>");
  text = text.replace(/^# (.*$)/gm, "<h1><strong>$1</strong></h1>");
  text = text.replace(/^---$/gm, "<hr>");

  text = text.replace(/^(\s{2,4})- (.*$)/gm, '<li class="bullet_item">$2</li>');
  text = text.replace(/^\* (.*$)/gm, '<li class="bullet_item">$1</li>');
  text = text.replace(/^- (.*$)/gm, '<li class="bullet_item">$1</li>');
  text = text.replace(
    /(<li class="bullet_item">.*?<\/li>)(\s*<li class="bullet_item">.*?<\/li>)*/gs,
    '<ul class="bullet_list">$&</ul>'
  );
  text = text.replace(
    /<ul class="bullet_list">\s*<ul class="bullet_list">/g,
    '<ul class="bullet_list">'
  );
  text = text.replace(/<\/ul>\s*<\/ul>/g, "</ul>");

  text = text.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
  text = text.replace(
    /\[(.*?)\]\((.*?)\)/g,
    '<a href="$2" target="_blank" class="text-blue-400 hover:underline">$1</a>'
  );

  return text;
};

const convertToHTML = async (processedText) => {
  const renderer = new marked.Renderer();
  renderer.paragraph = function (text) {
    return '<p class="mb-4">' + (text?.text || "") + "</p>";
  };

  marked.setOptions({
    renderer: renderer,
    gfm: true,
    breaks: true,
    pedantic: false,
    smartLists: true,
    smartypants: false,
  });

  let html = marked(processedText);

  html = html
    .replace(/<\/p>\s*<br>\s*<p>/g, "</p><p>")
    .replace(/<br>\s*<ul>/g, "<ul>")
    .replace(/<\/h([1-6])>\s*<br>/g, "</h$1>")
    .replace(/<p>\s*<\/p>/g, "");

  html = html.replace(
    /<ul class="bullet_list">\s*<ul class="bullet_list">/g,
    '<ul class="bullet_list">'
  );
  html = html.replace(/<\/ul>\s*<\/ul>/g, "</ul>");

  return html;
};

watch(
  () => props.markdownText,
  async () => {
    await processMarkdown();
  },
  { immediate: true }
);

onMounted(async () => {
  await processMarkdown();
});
</script>

<style scoped>
.markdown_content pre {
  white-space: pre-wrap;
}

.markdown_content :deep(p) {
  margin-bottom: 0.5rem !important;
  line-height: 1.6 !important;
}

.markdown_content :deep(p:last-child) {
  margin-bottom: 0 !important;
}

.markdown_content :deep(h1),
.markdown_content :deep(h2),
.markdown_content :deep(h3),
.markdown_content :deep(h4),
.markdown_content :deep(h5) {
  margin-top: 1rem !important;
  margin-bottom: 0.5rem !important;
  line-height: 1.3 !important;
  font-weight: 600 !important;
}

.markdown_content :deep(ul),
.markdown_content :deep(ol) {
  margin: 0.75rem 0 1rem 0 !important;
  padding-left: 2rem !important;
}

.markdown_content :deep(li) {
  margin-bottom: 0.5rem !important;
  line-height: 1.5 !important;
}

.markdown_content :deep(a) {
  color: #4299e1;
  text-decoration: none;
}

.markdown_content :deep(a:hover) {
  text-decoration: underline;
}

.markdown_content :deep(ul),
.markdown_content :deep(ol) {
  list-style: disc !important;
  padding-left: 2rem !important;
  margin: 0.5rem 0 !important;
}

.markdown_content :deep(ol) {
  list-style: decimal !important;
}

.markdown_content :deep(li) {
  display: list-item !important;
  margin-bottom: 0.25rem !important;
  line-height: 1.5 !important;
}

.markdown_content :deep(ul ul) {
  list-style: circle !important;
}

.markdown_content :deep(ul ul ul) {
  list-style: square !important;
}
</style>
